const API_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Hotel API Service
 * Handles all hotel-related API operations
 */
export class HotelService {
    
    /**
     * Get hotel bookings, optionally limited to one calendar year
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @param {number} [year] - Only bookings overlapping this year
     * @returns {Promise<Array>} Array of bookings
     */
    static async getAllBookings(authorizedFetch, year = null) {
        const qs = year ? `?year=${year}` : '';
        const response = await authorizedFetch(`${API_URL}/api/hotel${qs}`);

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return response.json();
    }

    /**
     * Get bookings that have not finished before the given date — i.e. stays
     * that are current or upcoming. Past stays are excluded server-side.
     * @param {string} from - YYYY-MM-DD
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Array>} Array of bookings
     */
    static async getActiveBookings(from, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/hotel?from=${from}`);

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return response.json();
    }

    /**
     * Get the owner signatures for one booking. These are excluded from list
     * responses because they are large base64 images.
     * @param {string} bookingId - Booking ID
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} { checkIn, checkOut } - data URLs or null
     */
    static async getBookingSignatures(bookingId, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/hotel/${bookingId}/signatures`);

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return response.json();
    }

    /**
     * Get booking counts across all bookings (not just the loaded year)
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} { total, current, upcoming, past }
     */
    static async getBookingStats(authorizedFetch) {
        const d = new Date();
        const today = [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
        const response = await authorizedFetch(`${API_URL}/api/hotel/stats?today=${today}`);

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return response.json();
    }

    /**
     * Create a new hotel check-in
     * @param {Object} bookingData - Check-in data to create
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Created check-in
     */
    static async createBooking(bookingData, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/hotel`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bookingData)
        });
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
        
        return response.json();
    }

    /**
     * Update an existing hotel check-in
     * @param {string} bookingId - Check-in ID
     * @param {Object} bookingData - Updated check-in data
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Updated check-in
     */
    static async updateBooking(bookingId, bookingData, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/hotel/${bookingId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bookingData)
        });
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
        
        return response.json();
    }

    /**
     * Delete a hotel check-in
     * @param {string} bookingId - Check-in ID
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<void>}
     */
    static async deleteBooking(bookingId, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/hotel/${bookingId}`, {
            method: 'DELETE'
        });
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
    }
}
